import { Injectable } from "@angular/core";
import { MASK_VECTORIZATION_CONCURRENCY } from "../../model/detection/detection.tokens";

// ── Marching-squares tables (main-thread fallback — see {@link maskVectorizerWorkerMain}) ─────

/** exitSide[caseIndex][entrySide] → exitSide (-1 = no edge). Sides: 0=top 1=right 2=bottom 3=left */
const EXIT_SIDE: ReadonlyArray<readonly number[]> = [
  [-1, -1, -1, -1], //  0
  [-1, -1, 3, 2], //  1  L↔B
  [-1, 2, 1, -1], //  2  B↔R
  [-1, 3, -1, 1], //  3  L↔R
  [1, 0, -1, -1], //  4  T↔R
  [1, 0, 3, 2], //  5  saddle (disconnected)
  [2, -1, 0, -1], //  6  T↔B
  [3, -1, -1, 0], //  7  T↔L
  [3, -1, -1, 0], //  8  T↔L
  [2, -1, 0, -1], //  9  T↔B
  [3, 2, 1, 0], // 10  saddle (disconnected)
  [1, 0, -1, -1], // 11  T↔R
  [-1, 3, -1, 1], // 12  L↔R
  [-1, 2, 1, -1], // 13  B↔R
  [-1, -1, 3, 2], // 14  L↔B
  [-1, -1, -1, -1], // 15
  [3, 2, 1, 0], // 16  case-5 connected
  [1, 0, 3, 2], // 17  case-10 connected
];

const DC = [0, 1, 0, -1];
const DR = [-1, 0, 1, 0];
const OPP = [2, 3, 0, 1];

/** Result of vectorizing one pixel-mask PNG. */
export interface VectorizedMask {
  /** Contour rings in doubled-grid space: real pixel pos = (hx/2, hy/2). */
  rings: Array<Array<[number, number]>>;
  /** Width of the source PNG in pixels. */
  w: number;
  /** Height of the source PNG in pixels. */
  h: number;
}

/** One renderable region: a filled outer boundary with zero or more unfilled holes cut out of it. */
export interface MaskPolygon {
  outer: Array<[number, number]>;
  holes: Array<Array<[number, number]>>;
}

/** Message a vectorization worker posts back once a job settles. */
interface WorkerResultMessage {
  result?: VectorizedMask | null;
  error?: string;
}

/**
 * Runs entirely inside a dedicated Web Worker (see {@link MaskVectorizerService.createWorker}):
 * decodes the mask PNG and traces its contours off the main thread, then posts the result back.
 *
 * Deliberately self-contained — every table and helper this needs is declared *inside* this
 * function body, never read from the module-level consts above. A Blob-worker is built from
 * `.toString()` of this function run as a standalone script (see `createWorker`). If it
 * referenced an outer-scope binding instead (e.g. the module-level `EXIT_SIDE`), a minifier
 * renaming that binding across the main bundle wouldn't touch the *string* baked into the
 * worker script, and the worker would throw a `ReferenceError` only in a minified production
 * build. Keeping everything local sidesteps that: a minifier renames every reference to a
 * function-local binding together, so the stringified source stays internally consistent no
 * matter how the surrounding module is minified.
 */
function maskVectorizerWorkerMain(): void {
  const exitSide: ReadonlyArray<readonly number[]> = [
    [-1, -1, -1, -1],
    [-1, -1, 3, 2],
    [-1, 2, 1, -1],
    [-1, 3, -1, 1],
    [1, 0, -1, -1],
    [1, 0, 3, 2],
    [2, -1, 0, -1],
    [3, -1, -1, 0],
    [3, -1, -1, 0],
    [2, -1, 0, -1],
    [3, 2, 1, 0],
    [1, 0, -1, -1],
    [-1, 3, -1, 1],
    [-1, 2, 1, -1],
    [-1, -1, 3, 2],
    [-1, -1, -1, -1],
    [3, 2, 1, 0],
    [1, 0, 3, 2],
  ];
  const dc = [0, 1, 0, -1];
  const dr = [-1, 0, 1, 0];
  const opp = [2, 3, 0, 1];

  function traceRings(data: Uint8ClampedArray, mw: number, mh: number): Array<Array<[number, number]>> {
    const step = 1;
    const cols = Math.ceil(mw / step);
    const rows = Math.ceil(mh / step);

    let channel = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) {
        channel = 3;
        break;
      }
    }

    const pw = cols + 3;
    const ph = rows + 3;
    const grid = new Uint8Array(pw * ph);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const px = c * step;
        const py = r * step;
        if (data[(py * mw + px) * 4 + channel] > 127) {
          grid[(r + 1) * pw + (c + 1)] = 1;
        }
      }
    }

    const cw = pw - 1;
    const ch = ph - 1;
    const caseIdx = new Uint8Array(cw * ch);

    for (let r = 0; r < ch; r++) {
      for (let c = 0; c < cw; c++) {
        const tl = grid[r * pw + c];
        const tr = grid[r * pw + (c + 1)];
        const br = grid[(r + 1) * pw + (c + 1)];
        const bl = grid[(r + 1) * pw + c];
        let ci = (tl << 3) | (tr << 2) | (br << 1) | bl;

        if (ci === 5 || ci === 10) {
          const mx = Math.round((c - 0.5) * step);
          const my = Math.round((r - 0.5) * step);
          const centreOn = mx >= 0 && mx < mw && my >= 0 && my < mh && data[(my * mw + mx) * 4] > 127;
          if (ci === 5 && centreOn) ci = 16;
          if (ci === 10 && centreOn) ci = 17;
        }

        caseIdx[r * cw + c] = ci;
      }
    }

    const visited = new Uint8Array(cw * ch);
    const rings: Array<Array<[number, number]>> = [];

    for (let r = 0; r < ch; r++) {
      for (let c = 0; c < cw; c++) {
        const cs = caseIdx[r * cw + c];
        if (cs === 0 || cs === 15) continue;

        for (let s = 0; s < 4; s++) {
          if (exitSide[cs][s] === -1) continue;
          if (visited[r * cw + c] & (1 << s)) continue;

          const ring: Array<[number, number]> = [];
          let cc = c;
          let cr = r;
          let entry = s;

          do {
            const i = cr * cw + cc;
            const ex = exitSide[caseIdx[i]][entry];
            visited[i] |= (1 << entry) | (1 << ex);

            switch (entry) {
              case 0:
                ring.push([2 * cc + 1, 2 * cr]);
                break;
              case 1:
                ring.push([2 * cc + 2, 2 * cr + 1]);
                break;
              case 2:
                ring.push([2 * cc + 1, 2 * cr + 2]);
                break;
              case 3:
                ring.push([2 * cc, 2 * cr + 1]);
                break;
              default:
                break;
            }

            cc += dc[ex];
            cr += dr[ex];
            entry = opp[ex];
          } while (cc !== c || cr !== r || entry !== s);

          if (ring.length >= 3) rings.push(ring);
        }
      }
    }

    return rings.map((ring) => ring.map(([hx, hy]) => [hx - 2, hy - 2] as [number, number]));
  }

  async function decodeAndTrace(
    base64Png: string,
  ): Promise<{ rings: Array<Array<[number, number]>>; w: number; h: number } | null> {
    const binary = atob(base64Png);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);

    const bitmap = await createImageBitmap(new Blob([bytes], { type: "image/png" }));
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      bitmap.close();
      return null;
    }
    ctx.drawImage(bitmap, 0, 0);
    const imageData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);
    bitmap.close();

    const rings = traceRings(imageData.data, imageData.width, imageData.height);
    return rings.length ? { rings, w: imageData.width, h: imageData.height } : null;
  }

  const workerScope = self as unknown as {
    onmessage: ((e: MessageEvent<{ base64Png: string }>) => void) | null;
    postMessage: (message: unknown) => void;
  };

  workerScope.onmessage = (e) => {
    decodeAndTrace(e.data.base64Png)
      .then((result) => workerScope.postMessage({ result }))
      .catch((err: unknown) => {
        workerScope.postMessage({ error: err instanceof Error ? err.message : String(err) });
      });
  };
}

/**
 * Converts a binary pixel-mask PNG (base64, white = foreground) into contour
 * rings using marching squares. Rings are returned in doubled-grid space so
 * both geographic projection (map layer) and percentage projection (card SVG)
 * can use the same normalisation: `hx / 2 / w` and `hy / 2 / h`.
 *
 * `vectorize()` runs the decode + trace in a pool of {@link MASK_VECTORIZATION_CONCURRENCY}
 * persistent Web Workers (see {@link maskVectorizerWorkerMain}), so a run with thousands of
 * detections never blocks the main thread — only falls back to running on the main thread
 * (the original, pre-worker implementation, kept below) when `Worker`/`OffscreenCanvas` aren't
 * available. Safe to use as a root-level singleton: the worker pool is created lazily on first
 * use and lives for the app's lifetime.
 */
@Injectable({ providedIn: "root" })
export class MaskVectorizerService {
  private readonly workersSupported = MaskVectorizerService.detectWorkerSupport();

  private pool: Worker[] = [];

  private readonly idleWorkers: Worker[] = [];

  private readonly queue: Array<{ base64Png: string; resolve: (result: VectorizedMask | null) => void }> = [];

  /**
   * Decodes `base64Png`, traces its contours and returns the rings.
   * Returns `null` if the image cannot be decoded or has no foreground pixels.
   */
  async vectorize(base64Png: string): Promise<VectorizedMask | null> {
    if (!this.workersSupported) return this.vectorizeOnMainThread(base64Png);
    this.ensurePool();
    return new Promise<VectorizedMask | null>((resolve) => {
      this.queue.push({ base64Png, resolve });
      this.pump();
    });
  }

  /**
   * Projects a doubled-grid point `[hx, hy]` (real pixel pos = `hx/2, hy/2`, canvas
   * of `w`×`h` pixels) into geographic `[lon, lat]`, using `bbox` (`[minLon, minLat,
   * maxLon, maxLat]`) as the linear frame the canvas is aligned with. Inverse of
   * {@link projectToGrid}.
   */
  static projectToGeo([hx, hy]: [number, number], w: number, h: number, bbox: [number, number, number, number]): [number, number] {
    const [minLon, minLat, maxLon, maxLat] = bbox;
    return [minLon + (hx / 2 / w) * (maxLon - minLon), maxLat - (hy / 2 / h) * (maxLat - minLat)];
  }

  /**
   * Projects a geographic `[lon, lat]` point back into doubled-grid pixel space for
   * a `w`×`h` canvas aligned with `bbox`. Inverse of {@link projectToGeo}.
   */
  static projectToGrid([lon, lat]: [number, number], w: number, h: number, bbox: [number, number, number, number]): [number, number] {
    const [minLon, minLat, maxLon, maxLat] = bbox;
    return [
      Math.round((2 * w * (lon - minLon)) / (maxLon - minLon)),
      Math.round((2 * h * (maxLat - lat)) / (maxLat - minLat)),
    ];
  }

  /**
   * Groups raw marching-squares rings into {@link MaskPolygon}s (outer boundary + its
   * holes), so renderers that need real polygon-with-holes geometry (e.g. WKT/OpenLayers,
   * which has no notion of an implicit SVG-style nonzero fill rule) can cut holes out
   * instead of painting every ring as its own opaque shape.
   *
   * Nesting is resolved by containment depth (point-in-ring), not by winding order —
   * robust regardless of the tracer's orientation convention. Rings at even depth
   * (0, 2, 4, ...) are foreground: depth 0 is a top-level outer boundary, depth 2 is an
   * "island" inside a hole and starts its own polygon. Rings at odd depth are holes of
   * their immediate (smallest enclosing) parent.
   */
  static groupRingsIntoPolygons(rings: ReadonlyArray<Array<[number, number]>>): MaskPolygon[] {
    const n = rings.length;
    if (n === 0) return [];
    if (n === 1) return [{ outer: rings[0], holes: [] }];

    const areas = rings.map((r) => Math.abs(this.signedArea(r)));
    const byAreaAsc = areas.map((_, i) => i).sort((a, b) => areas[a] - areas[b]);

    // Immediate parent = smallest ring (strictly bigger than this one) that contains it.
    const parent: number[] = new Array(n).fill(-1);
    for (const i of byAreaAsc) {
      const testPoint = rings[i][0];
      let bestParent = -1;
      let bestArea = Infinity;
      for (let j = 0; j < n; j++) {
        if (j === i || areas[j] <= areas[i]) continue;
        if (areas[j] < bestArea && this.pointInRing(testPoint, rings[j])) {
          bestParent = j;
          bestArea = areas[j];
        }
      }
      parent[i] = bestParent;
    }

    const depth: number[] = new Array(n).fill(0);
    for (const i of byAreaAsc) {
      depth[i] = parent[i] === -1 ? 0 : depth[parent[i]] + 1;
    }

    const polygons: MaskPolygon[] = [];
    for (let i = 0; i < n; i++) {
      if (depth[i] % 2 !== 0) continue; // odd depth = hole, attached to its parent below
      const holes = rings.filter((_, j) => parent[j] === i);
      polygons.push({ outer: rings[i], holes });
    }
    return polygons;
  }

  /** Shoelace signed area — sign flips between nested rings, magnitude used to rank nesting. */
  private static signedArea(ring: ReadonlyArray<[number, number]>): number {
    let sum = 0;
    for (let i = 0; i < ring.length; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[(i + 1) % ring.length];
      sum += x1 * y2 - x2 * y1;
    }
    return sum / 2;
  }

  /** Even-odd ray-casting point-in-polygon test (PNPOLY). */
  private static pointInRing([x, y]: [number, number], ring: ReadonlyArray<[number, number]>): boolean {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  // ── Worker pool ──────────────────────────────────────────────────────────

  private static detectWorkerSupport(): boolean {
    return (
      typeof Worker !== "undefined" && typeof OffscreenCanvas !== "undefined" && typeof createImageBitmap !== "undefined"
    );
  }

  /** Spins up the fixed-size worker pool on first use. No-op on subsequent calls. */
  private ensurePool(): void {
    if (this.pool.length) return;
    this.pool = Array.from({ length: MASK_VECTORIZATION_CONCURRENCY }, () => MaskVectorizerService.createWorker());
    this.idleWorkers.push(...this.pool);
  }

  /** Builds one dedicated vectorization worker from an inline Blob script — see {@link maskVectorizerWorkerMain}. */
  private static createWorker(): Worker {
    const source = `(${maskVectorizerWorkerMain.toString()})();`;
    const blob = new Blob([source], { type: "application/javascript" });
    return new Worker(URL.createObjectURL(blob));
  }

  /**
   * Dispatches queued jobs to idle workers until either runs out. Each worker only ever has
   * one job in flight, so job/response pairing needs no ids — the listeners registered here
   * are for exactly the one message this dispatch is waiting for.
   */
  private pump(): void {
    while (this.idleWorkers.length && this.queue.length) {
      const worker = this.idleWorkers.pop()!;
      const job = this.queue.shift()!;

      const cleanup = (): void => {
        worker.removeEventListener("message", onMessage);
        worker.removeEventListener("error", onError);
        this.idleWorkers.push(worker);
        this.pump();
      };
      const onMessage = (e: MessageEvent<WorkerResultMessage>): void => {
        cleanup();
        if (e.data?.error) {
          console.error("MaskVectorizerService: worker vectorisation failed:", e.data.error);
          job.resolve(null);
        } else {
          job.resolve(e.data?.result ?? null);
        }
      };
      const onError = (err: ErrorEvent): void => {
        cleanup();
        console.error("MaskVectorizerService: worker crashed:", err.message);
        job.resolve(null);
      };

      worker.addEventListener("message", onMessage);
      worker.addEventListener("error", onError);
      worker.postMessage({ base64Png: job.base64Png });
    }
  }

  // ── Main-thread fallback (Worker/OffscreenCanvas unavailable) ───────────────

  private async vectorizeOnMainThread(base64Png: string): Promise<VectorizedMask | null> {
    const imageData = await this.decodePng(base64Png);
    if (!imageData) return null;
    const rings = this.traceRings(imageData.data, imageData.width, imageData.height);
    return rings.length ? { rings, w: imageData.width, h: imageData.height } : null;
  }

  private decodePng(base64Png: string): Promise<ImageData | null> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(ctx.getImageData(0, 0, img.width, img.height));
      };
      img.onerror = () => resolve(null);
      img.src = `data:image/png;base64,${base64Png}`;
    });
  }

  private traceRings(data: Uint8ClampedArray, mw: number, mh: number): Array<Array<[number, number]>> {
    const step = 1;
    const cols = Math.ceil(mw / step);
    const rows = Math.ceil(mh / step);

    // Determine which channel encodes the mask:
    // - Standard grayscale PNGs: all pixels fully opaque (A=255), luminance in R
    // - Alpha-mask PNGs: car pixels opaque (A=255), background transparent (A=0)
    let channel = 0; // default: R (luminance)
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 255) {
        channel = 3; // transparent pixel found → use alpha
        break;
      }
    }

    // Padded binary grid (1-cell zero border on every side)
    const pw = cols + 3;
    const ph = rows + 3;
    const grid = new Uint8Array(pw * ph);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const px = c * step;
        const py = r * step;
        if (data[(py * mw + px) * 4 + channel] > 127) {
          grid[(r + 1) * pw + (c + 1)] = 1;
        }
      }
    }

    const cw = pw - 1;
    const ch = ph - 1;
    const caseIdx = new Uint8Array(cw * ch);

    for (let r = 0; r < ch; r++) {
      for (let c = 0; c < cw; c++) {
        const tl = grid[r * pw + c];
        const tr = grid[r * pw + (c + 1)];
        const br = grid[(r + 1) * pw + (c + 1)];
        const bl = grid[(r + 1) * pw + c];
        let ci = (tl << 3) | (tr << 2) | (br << 1) | bl;

        if (ci === 5 || ci === 10) {
          const mx = Math.round((c - 0.5) * step);
          const my = Math.round((r - 0.5) * step);
          const centreOn = mx >= 0 && mx < mw && my >= 0 && my < mh && data[(my * mw + mx) * 4] > 127;
          if (ci === 5 && centreOn) ci = 16;
          if (ci === 10 && centreOn) ci = 17;
        }

        caseIdx[r * cw + c] = ci;
      }
    }

    const visited = new Uint8Array(cw * ch);
    const rings: Array<Array<[number, number]>> = [];

    for (let r = 0; r < ch; r++) {
      for (let c = 0; c < cw; c++) {
        const cs = caseIdx[r * cw + c];
        if (cs === 0 || cs === 15) continue;

        for (let s = 0; s < 4; s++) {
          if (EXIT_SIDE[cs][s] === -1) continue;
          if (visited[r * cw + c] & (1 << s)) continue;

          const ring: Array<[number, number]> = [];
          let cc = c;
          let cr = r;
          let entry = s;

          do {
            const i = cr * cw + cc;
            const ex = EXIT_SIDE[caseIdx[i]][entry];
            visited[i] |= (1 << entry) | (1 << ex);

            switch (entry) {
              case 0:
                ring.push([2 * cc + 1, 2 * cr]);
                break;
              case 1:
                ring.push([2 * cc + 2, 2 * cr + 1]);
                break;
              case 2:
                ring.push([2 * cc + 1, 2 * cr + 2]);
                break;
              case 3:
                ring.push([2 * cc, 2 * cr + 1]);
                break;
              default:
                break;
            }

            cc += DC[ex];
            cr += DR[ex];
            entry = OPP[ex];
          } while (cc !== c || cr !== r || entry !== s);

          if (ring.length >= 3) rings.push(ring);
        }
      }
    }

    // Shift from padded back to original doubled-grid coordinates
    return rings.map((ring) => ring.map(([hx, hy]) => [hx - 2, hy - 2] as [number, number]));
  }
}
